import React, {
  type ComponentProps,
  useCallback,
  useLayoutEffect,
} from 'react';
import { defineMessages, useIntl } from 'react-intl';
import type { ViewProps } from 'react-native';
import {
  Platform,
  ScrollView,
  StyleSheet,
  useWindowDimensions,
  View,
} from 'react-native';
import { useStyle } from 'react-native-style-utilities';
import type { NavigationProp, ParamListBase } from '@react-navigation/native';
import type { AddressType } from '@sg/garnish';
import {
  AddressTypes,
  LocationCard,
  ModalRow,
  theme,
  useResponsive,
  useSafeExpoNavigation,
  useSafeNavigation,
} from '@sg/garnish';
import { Map } from '@sg/radar-fe';

import { useIsExpoRouterEnabled } from '@order/shared/hooks';
import { getEnvVars } from '@order/utils';

import {
  LoadingAnimation,
  NotFoundView,
  TransparentModal,
} from '../../../../components';
import type { ReorderRestaurantQuery } from '../../GraphQL/ReorderConfirmLocation.generated';
import {
  type ReorderDeliveryAddressQuery,
  useReorderDeliveryAddressQuery,
} from '../../GraphQL/ReorderDeliveryAddress.generated';
import {
  useCancelReorder,
  useChangeLocation,
  useConflictReview,
  useReorderState,
} from '../../hooks';
import {
  ReorderModalFooter,
  ReorderModalHeader,
  ReorderTitle,
  useReorderLeftIcon,
  useReorderRightIcon,
} from './ReorderConfirmLocation.components';
import { useReorderConfirmLocationData } from './ReorderConfirmLocation.data';

export const ReorderConfirmLocation = (params: ReorderConfirmLocationProps) => {
  const { navigation } = params;
  const { formatMessage } = useIntl();

  const {
    currentStep,
    reordering,
    restaurantId,
    orderType,
    deliveryOrderDetail,
  } = useReorderState();

  const cancelReorder = useCancelReorder();
  const changeLocation = useChangeLocation();
  const conflictReview = useConflictReview();

  const cancelReorderToBag = useCallback(() => {
    cancelReorder({ dismissToBagMenu: true });
  }, [cancelReorder]);

  const cancelReorderToHome = useCallback(() => {
    cancelReorder({ dismissToHome: true });
  }, [cancelReorder]);

  const { fetching, region, pins, restaurant } = useReorderConfirmLocationData(
    // @ts-expect-error TS(2345): Argument of type 'string | undefined' is not assig... Remove this comment to see the full error message
    restaurantId,
    orderType,
  );

  if (currentStep !== 'locationConfirmation' && Platform.OS === 'web') {
    return null;
  }

  if (fetching) {
    return (
      <ReorderConfirmLocationModal onClose={cancelReorderToBag}>
        <View style={styles.loadingContainer}>
          <LoadingAnimation />
        </View>
      </ReorderConfirmLocationModal>
    );
  }

  if (!restaurant) {
    return (
      <ReorderConfirmLocationModal onClose={cancelReorderToHome}>
        <NotFoundView.Wrapper withoutFooter>
          <NotFoundView.Image />
          <NotFoundView.Description
            description={formatMessage(messages.issueWithReordering)}
            sizeMatch={['22']}
            alignment="center"
          />
          <NotFoundView.Cta onBtnPress={cancelReorderToHome} />
        </NotFoundView.Wrapper>
      </ReorderConfirmLocationModal>
    );
  }

  return (
    <ReorderConfirmLocationContent
      navigation={navigation}
      fetching={fetching}
      // @ts-expect-error TS(2322): Type 'AddressType | undefined' is not assignable t... Remove this comment to see the full error message
      orderType={orderType}
      restaurant={restaurant}
      region={region}
      pins={pins}
      isLoading={reordering}
      cancelReorder={cancelReorderToBag}
      onClose={cancelReorderToBag}
      onContinue={conflictReview}
      onMoreLocations={changeLocation}
      deliveryAddressId={deliveryOrderDetail?.addressId}
    />
  );
};

export const ReorderConfirmLocationContent = (
  props: ReorderConfirmLocationContentProps,
) => {
  const { currentBreakpoint } = useResponsive();
  const Layout = currentBreakpoint.isXS ? MobileLayout : DesktopLayout;
  const modalSize = currentBreakpoint.isXS ? 'full' : 'large';

  const addressId = props.deliveryAddressId ?? '';

  const [{ data: addressData }] = useReorderDeliveryAddressQuery({
    variables: { addressId },
    pause: !addressId,
  });

  return (
    <TransparentModal
      size={modalSize}
      onClose={props.cancelReorder}
      mobileEdges={['bottom']}
    >
      <Layout {...props} deliveryAddress={addressData?.address}>
        {/* @ts-expect-error TS(2322): Type '{ fetching?: boolean | undefined; deliveryAd... Remove this comment to see the full error message */}
        <Map publishableKey={RADAR_SDK_KEY} {...props} />
      </Layout>
    </TransparentModal>
  );
};

const DesktopLayout = ({
  orderType,
  restaurant,
  deliveryAddress,
  isLoading,
  children,
  onClose,
  onMoreLocations,
  onContinue,
}: Omit<ReorderConfirmLocationLayoutProps, 'navigation'>) => {
  const isDelivery = orderType === AddressTypes.delivery;

  return (
    <View
      testID="reordering.confirm-location.container"
      style={styles.desktopContainer}
    >
      <View style={styles.desktopMapContainer}>{children}</View>
      <View style={styles.desktopContentContainer}>
        <ReorderModalHeader orderType={orderType} onClose={onClose} />
        <ModalRow>
          {isDelivery ? (
            <DeliveryLocationCard deliveryAddress={deliveryAddress} />
          ) : (
            <RestaurantLocationCard
              orderType={orderType}
              restaurant={restaurant}
            />
          )}
        </ModalRow>
        <ReorderModalFooter
          isLoading={isLoading}
          onMoreLocations={onMoreLocations}
          onContinue={onContinue}
        />
      </View>
    </View>
  );
};

const MobileLayout = ({
  navigation,
  orderType,
  restaurant,
  deliveryAddress,
  isLoading,
  children,
  onMoreLocations,
  onContinue,
}: Omit<ReorderConfirmLocationLayoutProps, 'onClose'>) => {
  const { width } = useWindowDimensions();
  const dynamicHeight = useStyle(() => ({ height: width * 0.5 }), [width]);
  const isDelivery = orderType === AddressTypes.delivery;

  // Mobile Header
  const isExpoRouterEnabled = useIsExpoRouterEnabled();
  const leftIcon = useReorderLeftIcon();
  const rightIcon = useReorderRightIcon();
  const safeNav = useSafeNavigation();
  const expoNav = useSafeExpoNavigation();
  const setOptions =
    navigation?.setOptions ?? safeNav?.setOptions ?? expoNav?.setOptions;

  useLayoutEffect(() => {
    setOptions?.({
      headerTitle: () => <ReorderTitle orderType={orderType} />,
      headerLeft: isExpoRouterEnabled && leftIcon ? () => leftIcon : undefined,
      headerRight: () => rightIcon,
      headerShown: true,
    });
  }, [setOptions, orderType, isExpoRouterEnabled, leftIcon, rightIcon]);

  return (
    <View
      testID="reordering.confirm-location.container"
      style={styles.mobileContainer}
    >
      <ScrollView
        testID="reordering.confirm-location.contents"
        style={styles.mobileContent}
        contentContainerStyle={styles.grow}
      >
        <ModalRow>
          <View style={[styles.mobileMapContainer, dynamicHeight]}>
            {children}
          </View>
        </ModalRow>
        <ModalRow style={styles.mobileLocationContainer}>
          {isDelivery ? (
            <DeliveryLocationCard deliveryAddress={deliveryAddress} />
          ) : (
            <RestaurantLocationCard
              orderType={orderType}
              restaurant={restaurant}
            />
          )}
        </ModalRow>
      </ScrollView>
      <ReorderModalFooter
        isLoading={isLoading}
        onMoreLocations={onMoreLocations}
        onContinue={onContinue}
      />
    </View>
  );
};

const RestaurantLocationCard = ({
  restaurant,
  orderType,
}: RestaurantLocationCardProps) => {
  return (
    <LocationCard
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      id={restaurant.id}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      acceptingOrders={restaurant.isAcceptingOrders}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      notAcceptingOrdersReason={restaurant.notAcceptingOrdersReason}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      restaurantSlug={restaurant.slug}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      address={restaurant.address}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      city={restaurant.city}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      state={restaurant.state}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      zipCode={restaurant.zipCode}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      name={restaurant.name}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      flexMessage={restaurant.flexMessage ?? ''}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      lat={restaurant.latitude}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      lng={restaurant.longitude}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      storeHours={restaurant.hours?.formatted ?? ''}
      // @ts-expect-error TS(2531): Object is possibly 'null'.
      imageUrl={orderType === 'outpost' ? undefined : restaurant.asset?.url}
    />
  );
};

const DeliveryLocationCard = ({
  deliveryAddress,
}: DeliveryLocationCardProps) => {
  if (!deliveryAddress) return null;

  const deliveryAddressStreet = `${deliveryAddress?.street}${
    deliveryAddress?.secondaryStreet
      ? `, ${deliveryAddress?.secondaryStreet}`
      : ''
  }`;

  return (
    <LocationCard
      id={deliveryAddress?.id ?? ''}
      acceptingOrders={true}
      notAcceptingOrdersReason=""
      restaurantSlug=""
      address={deliveryAddressStreet}
      city={deliveryAddress?.city ?? ''}
      state={deliveryAddress?.state ?? ''}
      zipCode={deliveryAddress?.zipCode ?? ''}
      name={deliveryAddress?.name ?? ''}
      flexMessage=""
      lat={deliveryAddress?.latitude ?? 0}
      lng={deliveryAddress?.longitude ?? 0}
      storeHours=""
    />
  );
};

// ─────────────────────────────────────────────────────────────────────────────

const ReorderConfirmLocationModal = (
  props: ComponentProps<typeof TransparentModal>,
) => {
  const { children, ...restProps } = props;

  const { match } = useResponsive();
  const modalSize = match(['full', 'large'] as const);

  return (
    <TransparentModal
      size={modalSize}
      mobileEdges={SAFE_AREA_EDGES}
      {...restProps}
    >
      {children}
    </TransparentModal>
  );
};

// ─── Messages ────────────────────────────────────────────────────────────────

const messages = defineMessages({
  issueWithReordering: {
    defaultMessage:
      "We're sorry, but there was an issue reordering that item, please try placing an order from scratch.",
    description: 'Reorder | Confirm location | Issue with reordering',
  },
});

// ─── Constants ───────────────────────────────────────────────────────────────

const { RADAR_SDK_KEY } = getEnvVars();
const SAFE_AREA_EDGES = ['bottom'] as const;

// ─── Types ───────────────────────────────────────────────────────────────────

type ReorderConfirmLocationContentProps = Readonly<{
  fetching?: boolean;
  deliveryAddressId?: string;
  cancelReorder: () => void;
}> &
  ComponentProps<typeof Map> &
  ReorderConfirmLocationLayoutProps;

type ReorderConfirmLocationProps = {
  navigation?: NavigationProp<ParamListBase>;
};

type ReorderConfirmLocationLayoutProps = Readonly<{
  navigation?: NavigationProp<ParamListBase>;
  orderType: AddressType;
  restaurant: ReorderRestaurantQuery['restaurant'];
  isLoading?: boolean;
  children?: ViewProps['children'];
  deliveryAddress?: ReorderDeliveryAddressQuery['address'];
  onClose: () => void;
  onMoreLocations: () => void;
  onContinue: () => void;
}>;

type RestaurantLocationCardProps = Readonly<{
  restaurant: ReorderRestaurantQuery['restaurant'];
  orderType: AddressType;
}>;

type DeliveryLocationCardProps = Readonly<{
  deliveryAddress?: ReorderDeliveryAddressQuery['address'];
}>;

// ─────────────────────────────────────────────────────────────────────────────

const styles = StyleSheet.create({
  loadingContainer: {
    flex: 1,
    height: '100%',
    padding: theme.spacing['20'],
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  desktopContainer: {
    minHeight: 504,
    flex: 1,
    flexDirection: 'row',
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  mobileContainer: {
    flex: 1,
    backgroundColor: theme.colors.APP_BACKGROUND,
  },
  mobileContent: {
    flex: 1,
  },
  grow: {
    flexGrow: 1,
  },
  desktopMapContainer: {
    borderRightWidth: 1,
    borderRightColor: theme.colors.DARK_KALE,
    width: '50%',
    height: '100%',
  },
  desktopContentContainer: {
    flex: 1,
    width: '50%',
    height: '100%',
    justifyContent: 'space-around',
  },
  mobileMapContainer: {
    height: 192,
    borderRadius: theme.radius.medium,
    borderWidth: 1,
    borderColor: theme.colors.GRAY,
    overflow: 'hidden',
  },
  mobileLocationContainer: {
    paddingVertical: theme.spacing['4'],
  },
});
